import React from "react";
import { Avatar, Box, MenuItem, Select, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUserRole } from "../../hooks/useUserRole";
import type { ClientPortalCompany } from "../../store/apis/clientPortalApi";
import { useClientCompany } from "./useClientCompanyViewClient";

const CompanyOption: React.FC<{ company: ClientPortalCompany }> = ({
  company,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
    <Avatar
      src={company.logo ?? undefined}
      alt=""
      aria-hidden
      variant="rounded"
      sx={{
        width: 22,
        height: 22,
        borderRadius: "6px",
        fontSize: 11,
        fontWeight: 700,
        bgcolor: "neutral.200",
        color: "text.primary",
        "& img": { objectFit: "contain" },
      }}
    >
      {company.name.charAt(0).toUpperCase()}
    </Avatar>
    <Box
      component="span"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {company.name}
    </Box>
  </Box>
);

/**
 * Selector de empresa del inicio para los roles que no son CLIENT: su sidebar
 * no tiene el del cliente. Con una sola empresa no se enseña.
 */
const CompanyPickerViewClient: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isClient } = useUserRole();
  const { companies, company, selectCompany } = useClientCompany();

  if (isClient || companies.length < 2 || !company) return null;

  return (
    <Select
      size="small"
      value={company.id}
      onChange={(event) => selectCompany(String(event.target.value))}
      renderValue={() => <CompanyOption company={company} />}
      inputProps={{ "aria-label": t("clientView.sidebar.switchCompany") }}
      MenuProps={{ slotProps: { paper: { sx: { maxHeight: 420 } } } }}
      sx={{
        minWidth: 220,
        maxWidth: 320,
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        color: "text.primary",
        backgroundColor: theme.palette.background.paper,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.divider,
        },
      }}
    >
      {companies.map((option) => (
        <MenuItem key={option.id} value={option.id} sx={{ fontSize: 14 }}>
          <CompanyOption company={option} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default CompanyPickerViewClient;
